import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, Spin, Tabs, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Course from '../../course/types/Course';
import SearchAPI, { SearchParams } from '../../search/SearchAPI';
import DistanceBadge from '../../search/component/DistanceBadge';
import InstructorResultCell from '../../search/component/InstructorResultCell';
import SearchCalendar from '../../search/component/SearchCalendar';
import SearchResult, {
  CalendarSearchResult,
} from '../../search/types/SearchResult';
import { Geolocation } from '../../types/Location';
import { formatTime } from '../../utils/date';

type OpenClassInstructorSearchProps = {
  courseId?: Course['id'];
  geolocation: Geolocation | null;
  placeId: string | null;
  startAt: Dayjs | null;
  endAt: Dayjs | null;
  minLength: number;
  inHouseOnly: boolean;
  maxDistance: boolean;
  selectedInstructorIds: number[];
  onAdd: (instructorId: number) => void;
};

const InstructorResult: React.FC<{
  result: SearchResult;
  added: boolean;
  onAdd: (instructorId: number) => void;
}> = ({ result, added, onAdd }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between border-0 border-b border-solid border-gray-100 py-3">
      <InstructorResultCell result={result} />
      <div className="flex items-center gap-6">
        <div className="text-sm text-blueGrayDark">
          {formatTime(result.start)} – {formatTime(result.end)}
        </div>
        <DistanceBadge
          distance={result.distance}
          mileage={result.mileage}
          maxTravelDistance={result.user.maxTravelDistance}
        />
        <Tooltip
          title={added ? t('views.OpenClasses.alreadyAssigned') : undefined}>
          <Button
            disabled={added}
            icon={<PlusOutlined />}
            onClick={() => onAdd(result.user.id)}>
            {t('views.OpenClasses.addInstructor')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

const InstructorResults: React.FC<{
  results: SearchResult[];
  loading: boolean;
  selectedInstructorIds: number[];
  onAdd: (instructorId: number) => void;
}> = ({ results, loading, selectedInstructorIds, onAdd }) => {
  const { t } = useTranslation();

  if (loading) {
    return <Spin size="large" className="my-8 flex justify-center" />;
  }
  if (results.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('views.OpenClasses.noAvailableInstructors')}
      />
    );
  }
  return (
    <div>
      {results.map((result) => (
        <InstructorResult
          key={result.user.id}
          added={selectedInstructorIds.includes(result.user.id)}
          result={result}
          onAdd={onAdd}
        />
      ))}
    </div>
  );
};

const OpenClassInstructorSearch: React.FC<OpenClassInstructorSearchProps> = ({
  courseId,
  geolocation,
  placeId,
  startAt,
  endAt,
  minLength,
  inHouseOnly,
  maxDistance,
  selectedInstructorIds,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('list');
  const [listResults, setListResults] = useState<SearchResult[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarSearchResult[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [drillDay, setDrillDay] = useState<Dayjs | null>(null);
  const [drillResults, setDrillResults] = useState<SearchResult[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const ready = courseId != null && !!geolocation && !!startAt && !!endAt;

  useEffect(() => {
    if (!ready) {
      setHasSearched(false);
      setListResults([]);
      setCalendarEvents([]);
      setDrillDay(null);
      setDrillResults([]);
    }
  }, [ready]);

  const showResults = hasSearched && ready;

  const baseParams = useMemo<SearchParams | null>(
    () =>
      ready
        ? {
            courseId: courseId!,
            lat: geolocation!.lat,
            lng: geolocation!.lng,
            placeId: placeId ?? undefined,
            minLength,
            internal: inHouseOnly,
            maxDistance,
            useCourseInstanceLimit: true,
            fetchMileage: true,
          }
        : null,
    [
      courseId,
      geolocation,
      inHouseOnly,
      maxDistance,
      minLength,
      placeId,
      ready,
    ],
  );

  const runSearch = useCallback(async () => {
    if (!baseParams || !startAt || !endAt) return;
    setHasSearched(true);
    setLoading(true);
    try {
      const [list, calendar] = await Promise.all([
        SearchAPI.search({
          ...baseParams,
          from: startAt.startOf('day').toISOString(),
          to: endAt.endOf('day').toISOString(),
        }),
        SearchAPI.calendarSearch({
          ...baseParams,
          month: startAt.format('YYYY-MM'),
        }),
      ]);
      setListResults(list.data.data);
      setCalendarEvents(calendar.data);
    } finally {
      setLoading(false);
    }
  }, [baseParams, endAt, startAt]);

  const openDay = useCallback(
    async (date: Date | null) => {
      if (!baseParams || !date) return;
      const day = dayjs(date);
      setDrillDay(day);
      setDrillResults([]);
      setDrillLoading(true);
      try {
        const { data } = await SearchAPI.search({
          ...baseParams,
          from: day.startOf('day').toISOString(),
          to: day.endOf('day').toISOString(),
        });
        setDrillResults(data.data);
      } finally {
        setDrillLoading(false);
      }
    },
    [baseParams],
  );

  // The search needs coordinates and a window, so until then the panel says what to fill in.
  const emptyPrompt = ready
    ? t('views.OpenClasses.pressSearchToSeeCalendar')
    : t('views.OpenClasses.searchNeedsLocationAndTime');

  return (
    <div>
      <div className="mb-4 mt-4 flex justify-end">
        <Tooltip
          title={
            ready
              ? undefined
              : t('views.OpenClasses.searchNeedsLocationAndTime')
          }>
          <span className="inline-block">
            <Button
              disabled={!ready}
              loading={loading}
              size="large"
              type="primary"
              onClick={runSearch}>
              {t('views.OpenClasses.searchInstructor')}
            </Button>
          </span>
        </Tooltip>
      </div>

      <Tabs
        activeKey={activeTab}
        items={[
          {
            key: 'list',
            label: t('views.OpenClasses.list'),
            children: showResults ? (
              <InstructorResults
                loading={loading}
                results={listResults}
                selectedInstructorIds={selectedInstructorIds}
                onAdd={onAdd}
              />
            ) : (
              <div className="py-10 text-center text-blueGrayDark">
                {emptyPrompt}
              </div>
            ),
          },
          {
            key: 'calendar',
            label: t('views.OpenClasses.calendar'),
            children: showResults ? (
              <SearchCalendar
                fetchedEvents={calendarEvents}
                loadingSearchResults={loading}
                onClickDay={openDay}
              />
            ) : (
              <div className="py-10 text-center text-blueGrayDark">
                {emptyPrompt}
              </div>
            ),
          },
        ]}
        onChange={setActiveTab}
      />

      <Modal
        footer={null}
        open={drillDay != null}
        title={
          drillDay
            ? t('views.OpenClasses.availableInstructorsOn', {
                date: drillDay.format('dddd D MMMM'),
              })
            : undefined
        }
        width={720}
        onCancel={() => setDrillDay(null)}>
        <InstructorResults
          loading={drillLoading}
          results={drillResults}
          selectedInstructorIds={selectedInstructorIds}
          onAdd={onAdd}
        />
      </Modal>
    </div>
  );
};

export default OpenClassInstructorSearch;
